import { Badge, Box, Button, Icon, TextLink } from '@palmetto/palmetto-components';

import { FlagPurposeType } from 'types';

const flagPurposeTypeMap: {
  [
    key in
      | FlagPurposeType.rejection
      | FlagPurposeType.stipulation
      | FlagPurposeType.additionalReview
      | FlagPurposeType.changeOrder
  ]: {
    label: string;
    variant: 'danger' | 'warning' | 'default' | 'info';
  };
} = {
  [FlagPurposeType.rejection]: {
    label: 'Rejection',
    variant: 'danger',
  },
  [FlagPurposeType.stipulation]: {
    label: 'Stipulation',
    variant: 'warning',
  },
  [FlagPurposeType.additionalReview]: {
    label: 'Additional Review',
    variant: 'default',
  },
  [FlagPurposeType.changeOrder]: {
    label: 'Change Order',
    variant: 'info',
  },
} as const;
interface FlagStatusDisplayProps {
  flag: any;
  currentPurposeType: FlagPurposeType;
  isReviewer: boolean;
  handleOpen: () => void;
  matchedCriteria: any;
  flagLabel: string;
}

export const FlagStatusDisplay = ({
  flag,
  currentPurposeType,
  isReviewer,
  handleOpen,
  matchedCriteria,
  flagLabel,
}: FlagStatusDisplayProps) => {
  return (
    <Box width={{ base: '100', desktop: '30' }}>
      {[
        FlagPurposeType.rejection,
        FlagPurposeType.stipulation,
        FlagPurposeType.additionalReview,
        FlagPurposeType.changeOrder,
      ].includes(currentPurposeType) ? (
        <Box padding="0 0 lg 0" fontSize="xs">
          <Box direction="row" alignItems="center" childGap="xs">
            {isReviewer && (
              <Button variant="tertiary" tone="neutral" iconPrefix="pencil" size="sm" onClick={handleOpen} />
            )}
            <Box fontWeight="medium" fontSize="lg">
              Flag Type:
            </Box>
          </Box>
          <Box padding="sm 0 0 0" width="xl" alignItems="center" direction="row" childGap="xs">
            <Badge
              variant={flagPurposeTypeMap[currentPurposeType as keyof typeof flagPurposeTypeMap].variant}
              message={flagPurposeTypeMap[currentPurposeType as keyof typeof flagPurposeTypeMap].label}
              size={{ base: 'md', desktop: 'lg' }}
            />
          </Box>
        </Box>
      ) : null}
      <Box childGap="xs" fontSize="xs" radius="md" padding="0 3xl 0 0">
        <Box padding="0 0 lg 0">
          <Box direction="row" alignItems="center">
            {isReviewer && (
              <Button variant="tertiary" tone="neutral" iconPrefix="pencil" size="sm" onClick={handleOpen} />
            )}
            <Box fontWeight="medium" fontSize="lg">
              Requirement missed:
            </Box>
          </Box>
          <Box display="flex" direction="column" gap="xs">
            {flag?.problems?.length > 0 ? (
              <>
                <Box padding="sm" style={{ lineHeight: 'var(--size-line-height-text)' }}>
                  <Box fontWeight="medium" fontSize="md">
                    {flagLabel}
                  </Box>
                  <Box
                    as="ul"
                    fontWeight="light"
                    fontSize="md"
                    padding="xs 0 0 lg"
                    style={{ margin: 0, listStyleType: 'disc', listStylePosition: 'outside' }}
                  >
                    {flag.problems.map((problem: any, index: number) => (
                      <li key={`reason-${index}`}>
                        {problem.displayText}
                        {problem.details?.length > 0 && ` - ${problem.details.join('; ')}`}
                      </li>
                    ))}
                  </Box>
                  <Box fontWeight="medium" fontSize="lg" padding="md 0 0 0">
                    Requirement:
                  </Box>
                  <Box display="block" fontWeight="light" fontSize="md" padding="xs 0 0 0">
                    {matchedCriteria?.description?.trim()
                      ? matchedCriteria.description
                      : 'No description available for this requirement.'}{' '}
                    {matchedCriteria?.knowledgeBaseArticle && (
                      <TextLink
                        href={matchedCriteria.knowledgeBaseArticle}
                        target="_blank"
                        style={{
                          cursor: 'pointer',
                          color: 'var(--color-text-body-primary)',
                          textDecoration: 'underline',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.2em',
                        }}
                      >
                        Check the Knowledge Base
                        <Icon name="launch-app" style={{ fontSize: 'inherit', lineHeight: 1 }} />
                      </TextLink>
                    )}
                  </Box>
                </Box>
              </>
            ) : flag?.reason ? (
              <Box
                padding="sm"
                radius="sm"
                fontWeight="light"
                fontSize="md"
                borderWidth="0 0 0 md"
                style={{ lineHeight: 'var(--size-line-height-text)' }}
              >
                {flag.reason}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
